import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import translate from '../i18n/config';
import { Modules, AuthRoutes, PermissionActionsEnum, PermissionModulesEnum, RouteParamsTypes } from '../enums';
import { Menu } from '../interfaces/common';
import { getCurrentMonthAndYear } from './methods';

export const TABLE_CONSTANTS = {
  defaultPerPage: 10,
  noRecordsFound: translate.t('common.table.noRecordsFound'),
};

export const Regex = {
  email: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d])(?=.*[^A-Za-z0-9\s])\S{6,15}$/,
  floatValues: /^\d+(\.\d{1,2})?$/,
  floatWithNegative: /^-?\d+(\.\d{1,2})?$/,
  percentPattern: /^(?:\d{1,2}(?:\.\d{1,2})?|100(?:\.0{1,2})?)$/
};

export const MenuTextMapping = {
  dashboard: 'dashboard',
  users: 'users',
  customers: 'customers',
  properties: 'properties',
  vendors: 'vendors',
  resources: 'resources',
  logout: 'logout',
  transactions: 'transactions',
  reservations: 'reservations',
  invoices: 'invoices',
  reconciliation: 'reconciliation',
  blank: '',
};

export const RoutesMapping = {
  [MenuTextMapping.dashboard]: [AuthRoutes.dashboard],
  [MenuTextMapping.users]: [AuthRoutes.users],
  [MenuTextMapping.customers]: [AuthRoutes.customers],
  [MenuTextMapping.properties]: [AuthRoutes.properties],
  [MenuTextMapping.vendors]: [AuthRoutes.vendors],
  [MenuTextMapping.resources]: [AuthRoutes.users, AuthRoutes.vendors, AuthRoutes.customers, AuthRoutes.properties],
  [MenuTextMapping.transactions]: [Modules.transactions],
  [MenuTextMapping.reservations]: [Modules.reservations],
  [MenuTextMapping.invoices]: [Modules.invoices],
  [MenuTextMapping.reconciliation]: [Modules.reconciliation],
};

export const ROUTES_PERMISSIONS_BINDING = {
  users: [{ [PermissionModulesEnum.Users]: [PermissionActionsEnum.View, PermissionActionsEnum.Add, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  vendors: [{ [PermissionModulesEnum.Vendor]: [PermissionActionsEnum.View, PermissionActionsEnum.Add, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  customers: [{ [PermissionModulesEnum.Customers]: [PermissionActionsEnum.View, PermissionActionsEnum.Add, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  properties: [{ [PermissionModulesEnum.Property]: [PermissionActionsEnum.View, PermissionActionsEnum.Add, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  transactions: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.View, PermissionActionsEnum.Add, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  reservations: [{ [PermissionModulesEnum.Reservation]: [PermissionActionsEnum.View, PermissionActionsEnum.Update, PermissionActionsEnum.Delete] }],
  invoices: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.Add, PermissionActionsEnum.View, PermissionActionsEnum.Update, PermissionActionsEnum.Delete, PermissionActionsEnum.Email, PermissionActionsEnum.Complete, PermissionActionsEnum.Verify] }],
  reconciliation: [{ [PermissionModulesEnum.Reconciliation]: [PermissionActionsEnum.View] }],
};

export const Menus: Menu[] = [
  {
    text: translate.t('layout.pageTexts.menus.dashboard'),
    id: MenuTextMapping.dashboard,
    redirectsTo: Modules.home + AuthRoutes.dashboard,
    icon: <i className="menu-icon tf-icons ri-home-smile-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.resources.users'),
    id: MenuTextMapping.users,
    sectionLabel: 'RESOURCES',
    redirectsTo: Modules.resources + AuthRoutes.users,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.users,
    icon: <i className="menu-icon tf-icons ri-user-3-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.resources.customers'),
    id: MenuTextMapping.customers,
    redirectsTo: Modules.resources + AuthRoutes.customers,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.customers,
    icon: <i className="menu-icon tf-icons ri-group-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.resources.properties'),
    id: MenuTextMapping.properties,
    redirectsTo: Modules.resources + AuthRoutes.properties,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.properties,
    icon: <i className="menu-icon tf-icons ri-building-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.resources.vendors'),
    id: MenuTextMapping.vendors,
    redirectsTo: Modules.resources + AuthRoutes.vendors,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.vendors,
    icon: <i className="menu-icon tf-icons ri-store-2-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.reservations'),
    id: MenuTextMapping.reservations,
    sectionLabel: 'OPERATIONS',
    redirectsTo: Modules.reservations,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.reservations,
    icon: <i className="menu-icon tf-icons ri-calendar-todo-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.transactions'),
    id: MenuTextMapping.transactions,
    redirectsTo: Modules.transactions,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.transactions,
    icon: <i className="menu-icon tf-icons ri-exchange-dollar-line"></i>,
  },
  {
    text: translate.t('layout.pageTexts.menus.invoices'),
    id: MenuTextMapping.invoices,
    redirectsTo: Modules.invoices,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.invoices,
    icon: <ReceiptOutlinedIcon className="menu-icon tf-icons ri-24px" />,
  },
  {
    text: translate.t('layout.pageTexts.menus.reconciliation.payout'),
    id: MenuTextMapping.reconciliation,
    sectionLabel: 'FINANCE',
    redirectsTo: Modules.reconciliation + RouteParamsTypes.payout,
    accessibleTo: ROUTES_PERMISSIONS_BINDING.reconciliation,
    icon: <i className="menu-icon tf-icons ri-scales-3-line"></i>,
  },
];

export const VALIDATION_FIELD_MAPPING = {
  username: 'username',
  first_name: 'first_name',
  password: 'password',
  email: 'email',
  oldPassword: 'oldPassword',
  newPassword: 'newPassword',
  confirmPassword: 'confirmPassword',
  role: 'role',
  manager: 'manager',
  updated_at: 'updated_at',
  actions: 'actions',
  searchText: 'searchText',
  changed_at: 'changed_at',
  changer_name: 'changer_name',
  field_name: 'field_name',
  old_value: 'old_value',
  new_value: 'new_value',
  firstName: 'firstName',
  lastName: 'lastName',
  phoneNumber: 'phoneNumber',
  phoneCountryCode: 'phoneCountryCode',
  emergencyPhone: 'emergencyPhone',
  emergencyPhoneCountryCode: 'emergencyPhoneCountryCode',
  countryCode: 'countryCode',
  order_by: 'order_by',
  name: 'name',
  address: 'address',
  status: 'status',
  currentPassword: "currentPassword",
  vendorId: "vendorId",
  updatedAt: "updatedAt",
  serviceType: 'serviceTypeId',
  addressCity: 'addressCity',
  addressState: 'addressState',
  addressPostcode: 'addressPostcode',
  addressCountry: 'addressCountry',
  serviceRate: 'serviceRate',
  notes: 'notes',
  address1: 'address1',
  address2: 'address2',
  customer: 'customer',
  customerName: 'customerName',
  publicName: 'publicName',
  hospitableId: 'hospitableId',
  pictureUrl: 'pictureUrl',
  customerId: 'customerId',
  managedBy: 'managedBy',
  assignedTo: 'assignedTo',
  cleaningManagedBy: 'cleaningManagedBy',
  primaryContact: 'primary_contact',
  primaryEmail: 'primaryEmail',
  primaryPhone: 'primaryPhone',
  primarySalutation: 'primarySalutation',
  primaryFirstName: 'primaryFirstName',
  primaryLastName: 'primaryLastName',
  secondaryEmail: 'secondaryEmail',
  displayName: 'displayName',
  companyName: 'companyName',
  website: 'website',
  hstNo: 'hstNo',
  billingCurrency: 'billingCurrency',
  billingAddress1: 'billingAddress1',
  billingAddress2: 'billingAddress2',
  billingCountry: 'billingCountry',
  billingState: 'billingState',
  billingCity: 'billingCity',
  billingPostal: 'billingPostal',
  property: 'property',
  commissionPercentage: 'commissionPercentage',
  commission: 'commission',
  configureMatTax: 'configureMatTax',
  matTaxPercentage: 'matTaxPercentage',
  bookingType: 'bookingType',
  whoCollectsMoney: 'whoCollectsMoney',
  whoCollectsHst: 'whoCollectsHst',
  matManagedBy: 'matManagedBy',
  maintenanceDetails: 'maintenanceDetails',
  city: 'city',
  image: 'image',
  state: 'state',
  country: 'country',
  postal: 'postal',
  myProperties: 'myProperties',
  user: 'user',
  transactionDate: 'transactionDate',
  propertyName: 'propertyName',
  transactionType: 'transactionType',
  debitCredit: 'debitCredit',
  invoiceNumber: 'invoiceNumber',
  vendorName: 'vendorName',
  totalAmount: 'totalAmount',
  propertyId: 'propertyId',
  expenseDetails: 'expenseDetails',
  actualCost: 'actualCost',
  clientCost: 'clientCost',
  reservationId: 'reservationId',
  paidBy: 'paidBy',
  carryforwardInvoiceNo: 'carryforwardInvoiceNo',
  chargeable: 'chargeable',
  reviewed: 'reviewed',
  reviewedBy: 'reviewedBy',
  attachments: 'attachments',
  includeNotes: 'includeNotes',
  isRecurring: 'isRecurring',
  isApproved: 'isApproved',
  isDeleted: 'isDeleted',
  lastUpdated: 'lastUpdated',
  propertyIds: 'propertyIds',
  vendorIds: 'vendorIds',
  startDate: 'startDate',
  endDate: 'endDate',
  hst: 'hst',
  subTotal: 'subTotal',
  hstAmount: 'hstAmount',
  transactionTypeIds: 'transactionTypeIds',
  settled: 'settled',
  unBilled: 'unbilled',
  invoice: 'invoiced',
  platform: 'platform',
  cities: 'cities',
  checkInDetails: 'checkInDetails',
  guest: 'guest',
  reservationStatus: 'reservationStatus',
  revenue: 'revenue',
  hostPayout: 'hostPayout',
  netRevenue: 'netRevenue',
  actualRevenue: 'actualRevenue',
  source: 'source',
  bookingDate: 'bookingDate',
  checkInDate: 'checkInDate',
  checkOutDate: 'checkOutDate',
  totalNights: 'totalNights',
  xNightRoomFees: 'xNightRoomFees',
  perNightFees: 'perNightFees',
  cleaningFees: 'cleaningFees',
  linenFees: 'linenFees',
  petFees: 'petFees',
  hostFees: 'hostFees',
  discount: 'discount',
  matTax: 'matTax',
  hstTax: 'hstTax',
  adjustments: 'adjustments',
  nights: 'nights',
  linkedWithHospitable: 'linkedWithHospitable',
  invoiceStatus: 'invoiceStatus',
  invoiceNo: 'invoiceNo',
  amountDue: 'amountDue',
  createdBy: 'createdBy',
  billingMonth: 'billingMonth',
  invoiceDate: 'invoiceDate',
  dueDate: 'dueDate',
  invoiceAmount: 'invoiceAmount',
  month: 'month',
  year: 'year',
  date: 'date',
  type: 'type',
  method: 'method',
  account: 'account',
  amount: 'amount',
  lastSync: 'lastSync',
  transactionDetails: 'transactionDetails',
  actualAmount: 'actualAmount',
  platformCommission: 'platformCommission',
  net: 'net',
  isActive: 'isActive',
  isVerified: 'isVerified',
  bankAccount: 'bankAccount',
  payoutType: 'payoutType',
  payoutStatus: 'payoutStatus',
  action: 'action',
  payoutManagedBy: 'payoutManagedBy',
  select: 'select',
  reservation: 'reservation',
  payoutDetails: 'payoutDetails',
  paymentDetails: 'paymentDetails',
  payoutAmt: 'payoutAmt',
  payoutMatchStatus: 'payoutMatchStatus',
  noteAction: 'noteAction',
};

export const HEAD_CELL_SORT_DISABLED = {
  userList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.manager,
    VALIDATION_FIELD_MAPPING.role,
  ],
  userAddEditProperties: [
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.address,
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.manager,
  ],
  userProfileProperties: [
    VALIDATION_FIELD_MAPPING.image,
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.city,
    VALIDATION_FIELD_MAPPING.managedBy,
  ],
  vendorList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.cities,
  ],
  customerList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.city,
  ],
  propertyList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.customer,
    VALIDATION_FIELD_MAPPING.managedBy
  ],
  assignPropertyModalList: [
    VALIDATION_FIELD_MAPPING.select,
    VALIDATION_FIELD_MAPPING.customer,
    VALIDATION_FIELD_MAPPING.managedBy,
  ],
  transactionList: [
    VALIDATION_FIELD_MAPPING.actions,
  ],
  platformListInPropertyForm: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.whoCollectsMoney,
    VALIDATION_FIELD_MAPPING.whoCollectsHst
  ],
  reservationList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.invoiceNumber
  ],
  invoiceList: [
    VALIDATION_FIELD_MAPPING.amountDue,
    VALIDATION_FIELD_MAPPING.actions,
  ],
  invoiceReservationList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.lastSync
  ],
  invoiceTransactionList: [
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.debitCredit,
    VALIDATION_FIELD_MAPPING.transactionType,
    VALIDATION_FIELD_MAPPING.transactionDetails,
    VALIDATION_FIELD_MAPPING.actualAmount
  ],
  invoicePaymentList: [
    VALIDATION_FIELD_MAPPING.notes,
    VALIDATION_FIELD_MAPPING.lastUpdated,
    VALIDATION_FIELD_MAPPING.actions,
  ],
  calculationTable: [
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.platformCommission,
    VALIDATION_FIELD_MAPPING.net,
  ],
  payoutList: [
    VALIDATION_FIELD_MAPPING.reservation,
    VALIDATION_FIELD_MAPPING.payoutDetails,
    VALIDATION_FIELD_MAPPING.select,
    VALIDATION_FIELD_MAPPING.noteAction,
  ],
  payoutAmtList: [
    VALIDATION_FIELD_MAPPING.date,
    VALIDATION_FIELD_MAPPING.paymentDetails,
    VALIDATION_FIELD_MAPPING.amount,
  ],
}

export const HEAD_CELL_LABELS_MAPPING = {
  userList: {
    [VALIDATION_FIELD_MAPPING.first_name]: translate.t('users.list.pageTexts.table.columns.user'),
    [VALIDATION_FIELD_MAPPING.role]: translate.t('users.list.pageTexts.table.columns.role'),
    [VALIDATION_FIELD_MAPPING.manager]: translate.t('users.list.pageTexts.table.columns.manager'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('users.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('users.list.pageTexts.table.columns.actions')
  },
  userAddEditProperties: {
    [VALIDATION_FIELD_MAPPING.name]: translate.t('users.addEdit.pageTexts.form.properties.table.columns.name'),
    [VALIDATION_FIELD_MAPPING.address]: translate.t('users.addEdit.pageTexts.form.properties.table.columns.address'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('users.addEdit.pageTexts.form.properties.table.columns.action'),
    [VALIDATION_FIELD_MAPPING.manager]: translate.t('users.addEdit.pageTexts.form.properties.table.columns.manager'),
  },
  userProfileProperties: {
    [VALIDATION_FIELD_MAPPING.image]: translate.t('users.profile.pageTexts.properties.table.columns.image'),
    [VALIDATION_FIELD_MAPPING.name]: translate.t('users.profile.pageTexts.properties.table.columns.name'),
    [VALIDATION_FIELD_MAPPING.city]: translate.t('users.profile.pageTexts.properties.table.columns.city'),
    [VALIDATION_FIELD_MAPPING.managedBy]: translate.t('users.profile.pageTexts.properties.table.columns.managedBy'),
  },
  vendorList: {
    [VALIDATION_FIELD_MAPPING.name]: translate.t('vendor.list.pageTexts.table.columns.name'),
    [VALIDATION_FIELD_MAPPING.phoneNumber]: translate.t('vendor.list.pageTexts.table.columns.phoneNumber'),
    [VALIDATION_FIELD_MAPPING.cities]: translate.t('vendor.list.pageTexts.table.columns.cities'),
    [VALIDATION_FIELD_MAPPING.serviceType]: translate.t('vendor.list.pageTexts.table.columns.vendorId'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('vendor.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('vendor.list.pageTexts.table.columns.actions')
  },
  customerList: {
    [VALIDATION_FIELD_MAPPING.primaryContact]: translate.t('customers.list.pageTexts.table.columns.primaryContact'),
    [VALIDATION_FIELD_MAPPING.primaryEmail]: translate.t('customers.list.pageTexts.table.columns.email'),
    [VALIDATION_FIELD_MAPPING.primaryPhone]: translate.t('customers.list.pageTexts.table.columns.primaryPhone'),
    [VALIDATION_FIELD_MAPPING.city]: translate.t('customers.list.pageTexts.table.columns.city'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('customers.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('customers.list.pageTexts.table.columns.actions'),
  },
  propertyList: {
    [VALIDATION_FIELD_MAPPING.name]: translate.t('property.list.pageTexts.table.columns.property'),
    [VALIDATION_FIELD_MAPPING.customer]: translate.t('property.list.pageTexts.table.columns.customer'),
    [VALIDATION_FIELD_MAPPING.managedBy]: translate.t('property.list.pageTexts.table.columns.managedBy'),
    [VALIDATION_FIELD_MAPPING.commissionPercentage]: translate.t('property.list.pageTexts.table.columns.commission'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('property.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('property.list.pageTexts.table.columns.actions'),
  },
  assignPropertyModalList: {
    [VALIDATION_FIELD_MAPPING.select]: translate.t('users.addEdit.pageTexts.assignPropertiesModal.table.columns.select'),
    [VALIDATION_FIELD_MAPPING.name]: translate.t('property.list.pageTexts.table.columns.property'),
    [VALIDATION_FIELD_MAPPING.customer]: translate.t('property.list.pageTexts.table.columns.customer'),
    [VALIDATION_FIELD_MAPPING.managedBy]: translate.t('property.list.pageTexts.table.columns.managedBy'),
    [VALIDATION_FIELD_MAPPING.commissionPercentage]: translate.t('property.list.pageTexts.table.columns.commission'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('property.list.pageTexts.table.columns.status'),
  },
  transactionList: {
    [VALIDATION_FIELD_MAPPING.transactionDate]: translate.t('transactions.list.pageTexts.table.columns.date'),
    [VALIDATION_FIELD_MAPPING.propertyName]: translate.t('transactions.list.pageTexts.table.columns.propertyName'),
    [VALIDATION_FIELD_MAPPING.invoiceNumber]: translate.t('transactions.list.pageTexts.table.columns.invoiceNumber'),
    [VALIDATION_FIELD_MAPPING.transactionType]: translate.t('transactions.list.pageTexts.table.columns.transactionType'),
    [VALIDATION_FIELD_MAPPING.debitCredit]: translate.t('transactions.list.pageTexts.table.columns.debitCredit'),
    [VALIDATION_FIELD_MAPPING.amount]: translate.t('transactions.list.pageTexts.table.columns.amount'),
    [VALIDATION_FIELD_MAPPING.totalAmount]: translate.t('transactions.list.pageTexts.table.columns.totalAmount'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('transactions.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('transactions.list.pageTexts.table.columns.actions'),
  },
  platformListInPropertyForm: {
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('property.addEdit.pageTexts.form.table.columns.actions'),
    [VALIDATION_FIELD_MAPPING.name]: translate.t('property.addEdit.pageTexts.form.table.columns.platform'),
    [VALIDATION_FIELD_MAPPING.whoCollectsMoney]: translate.t('property.addEdit.pageTexts.form.table.columns.whoCollectsMoney'),
    [VALIDATION_FIELD_MAPPING.whoCollectsHst]: translate.t('property.addEdit.pageTexts.form.table.columns.whoCollectsHst'),
    [VALIDATION_FIELD_MAPPING.matManagedBy]: translate.t('property.addEdit.pageTexts.form.table.columns.matManagedBy')
  },
  reservationList: {
    [VALIDATION_FIELD_MAPPING.property]: translate.t('reservations.list.pageTexts.table.columns.property'),
    [VALIDATION_FIELD_MAPPING.checkInDetails]: translate.t('reservations.list.pageTexts.table.columns.checkInDetails'),
    [VALIDATION_FIELD_MAPPING.guest]: translate.t('reservations.list.pageTexts.table.columns.guest'),
    [VALIDATION_FIELD_MAPPING.reservationStatus]: translate.t('reservations.list.pageTexts.table.columns.reservationStatus'),
    [VALIDATION_FIELD_MAPPING.hostPayout]: translate.t('reservations.list.pageTexts.table.columns.hostPayout'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('reservations.list.pageTexts.table.columns.status'),
    [VALIDATION_FIELD_MAPPING.invoiceNumber]: translate.t('reservations.list.pageTexts.table.columns.invoiceNumber'),
    [VALIDATION_FIELD_MAPPING.source]: translate.t('reservations.list.pageTexts.table.columns.source'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('reservations.list.pageTexts.table.columns.actions'),
  },
  invoiceList: {
    [VALIDATION_FIELD_MAPPING.invoiceStatus]: translate.t('invoices.addList.pageTexts.table.columns.invoiceStatus'),
    [VALIDATION_FIELD_MAPPING.invoiceNo]: translate.t('invoices.addList.pageTexts.table.columns.invoiceNo'),
    [VALIDATION_FIELD_MAPPING.amountDue]: translate.t('invoices.addList.pageTexts.table.columns.amountDue'),
    [VALIDATION_FIELD_MAPPING.customer]: translate.t('invoices.addList.pageTexts.table.columns.customer'),
    [VALIDATION_FIELD_MAPPING.property]: translate.t('invoices.addList.pageTexts.table.columns.property'),
    [VALIDATION_FIELD_MAPPING.invoiceDate]: translate.t('invoices.addList.pageTexts.table.columns.invoiceDate'),
    [VALIDATION_FIELD_MAPPING.dueDate]: translate.t('invoices.addList.pageTexts.table.columns.dueDate'),
    [VALIDATION_FIELD_MAPPING.commission]: translate.t('invoices.addList.pageTexts.table.columns.commission'),
    [VALIDATION_FIELD_MAPPING.invoiceAmount]: translate.t('invoices.addList.pageTexts.table.columns.invoiceAmount'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('invoices.addList.pageTexts.table.columns.actions'),
  },
  payoutList: {
    [VALIDATION_FIELD_MAPPING.select]: translate.t('reconciliation.payout.list.pageTexts.table.columns.select'),
    [VALIDATION_FIELD_MAPPING.property]: translate.t('reconciliation.payout.list.pageTexts.table.columns.property'),
    [VALIDATION_FIELD_MAPPING.reservation]: translate.t('reconciliation.payout.list.pageTexts.table.columns.reservation'),
    [VALIDATION_FIELD_MAPPING.hostPayout]: translate.t('reconciliation.payout.list.pageTexts.table.columns.hostPayout'),
    [VALIDATION_FIELD_MAPPING.payoutDetails]: translate.t('reconciliation.payout.list.pageTexts.table.columns.payoutDetails'),
    [VALIDATION_FIELD_MAPPING.payoutAmt]: translate.t('reconciliation.payout.list.pageTexts.table.columns.payoutAmt'),
    [VALIDATION_FIELD_MAPPING.payoutMatchStatus]: translate.t('reconciliation.payout.list.pageTexts.table.columns.payoutMatchStatus'),
    [VALIDATION_FIELD_MAPPING.action]: translate.t('reconciliation.payout.list.pageTexts.table.columns.action'),
    [VALIDATION_FIELD_MAPPING.noteAction]: translate.t('reconciliation.payout.list.pageTexts.table.columns.noteAction'),
  },
  payoutAmtList: {
    [VALIDATION_FIELD_MAPPING.date]: translate.t('reconciliation.payout.list.pageTexts.modals.payoutAmt.columns.date'),
    [VALIDATION_FIELD_MAPPING.paymentDetails]: translate.t('reconciliation.payout.list.pageTexts.modals.payoutAmt.columns.paymentDetails'),
    [VALIDATION_FIELD_MAPPING.amount]: translate.t('reconciliation.payout.list.pageTexts.modals.payoutAmt.columns.amount'),
  },
  invoiceReservationList: {
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.actions'),
    [VALIDATION_FIELD_MAPPING.checkInDetails]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.checkInDetails'),
    [VALIDATION_FIELD_MAPPING.guest]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.guest'),
    [VALIDATION_FIELD_MAPPING.reservationStatus]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.reservationStatus'),
    [VALIDATION_FIELD_MAPPING.hostPayout]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.hostPayout'),
    [VALIDATION_FIELD_MAPPING.source]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.source'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.status'),
    [VALIDATION_FIELD_MAPPING.lastSync]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.columns.lastSync'),
  },
  invoiceTransactionList: {
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.actions'),
    [VALIDATION_FIELD_MAPPING.transactionDate]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.date'),
    [VALIDATION_FIELD_MAPPING.debitCredit]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.debitCredit'),
    [VALIDATION_FIELD_MAPPING.transactionType]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.transactionType'),
    [VALIDATION_FIELD_MAPPING.transactionDetails]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.transactionDetails'),
    [VALIDATION_FIELD_MAPPING.actualAmount]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.actualAmount'),
    [VALIDATION_FIELD_MAPPING.amount]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.amount'),
    [VALIDATION_FIELD_MAPPING.status]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.status'),
    [VALIDATION_FIELD_MAPPING.lastUpdated]: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.columns.lastUpdated'),
  },
  invoicePaymentList: {
    [VALIDATION_FIELD_MAPPING.date]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.date'),
    [VALIDATION_FIELD_MAPPING.amount]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.amount'),
    [VALIDATION_FIELD_MAPPING.type]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.paymentType'),
    [VALIDATION_FIELD_MAPPING.method]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.method'),
    [VALIDATION_FIELD_MAPPING.account]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.account'),
    [VALIDATION_FIELD_MAPPING.notes]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.notes'),
    [VALIDATION_FIELD_MAPPING.lastUpdated]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.lastUpdated'),
    [VALIDATION_FIELD_MAPPING.actions]: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.columns.action'),
  },
  calculationTable: {
    [VALIDATION_FIELD_MAPPING.name]: translate.t('reservations.edit.pageTexts.form.calculationTable.head'),
    [VALIDATION_FIELD_MAPPING.amount]: translate.t('reservations.edit.pageTexts.form.calculationTable.amount'),
    [VALIDATION_FIELD_MAPPING.platformCommission]: translate.t('reservations.edit.pageTexts.form.calculationTable.platformCommission'),
    [VALIDATION_FIELD_MAPPING.net]: translate.t('reservations.edit.pageTexts.form.calculationTable.net'),
  },
};

export const HEAD_CELL_SORT_MAPPING = {
  userList: {
    [VALIDATION_FIELD_MAPPING.status]: 'is_active',
  },
  vendorList: {
    [VALIDATION_FIELD_MAPPING.status]: 'is_active',
    [VALIDATION_FIELD_MAPPING.phoneNumber]: 'phone',
    [VALIDATION_FIELD_MAPPING.serviceType]: 'service_type'
  },
  customerList: {
    [VALIDATION_FIELD_MAPPING.status]: 'is_active',
    [VALIDATION_FIELD_MAPPING.primaryContact]: 'primary_first_name',
    [VALIDATION_FIELD_MAPPING.primaryEmail]: 'primary_email',
    [VALIDATION_FIELD_MAPPING.phoneNumber]: 'country_code',
    [VALIDATION_FIELD_MAPPING.primaryPhone]: 'primary_phone'
  },
  propertyList: {
    [VALIDATION_FIELD_MAPPING.name]: 'name',
    [VALIDATION_FIELD_MAPPING.commissionPercentage]: 'commission_percentage',
    [VALIDATION_FIELD_MAPPING.status]: 'is_active',
  },
  transactionList: {
    [VALIDATION_FIELD_MAPPING.transactionDate]: 'date',
    [VALIDATION_FIELD_MAPPING.debitCredit]: 'debit_credit',
    [VALIDATION_FIELD_MAPPING.transactionType]: 'transaction_type',
    [VALIDATION_FIELD_MAPPING.propertyName]: 'property_name',
    [VALIDATION_FIELD_MAPPING.invoiceNumber]: 'invoice_no',
    [VALIDATION_FIELD_MAPPING.amount]: 'sub_total_amount',
    [VALIDATION_FIELD_MAPPING.totalAmount]: 'total_amount',
    [VALIDATION_FIELD_MAPPING.status]: 'transaction_status'
  },
  userProfileProperties: {
    [VALIDATION_FIELD_MAPPING.status]: 'is_active',
  },
  reservationList: {
    [VALIDATION_FIELD_MAPPING.property]: 'property_name',
    [VALIDATION_FIELD_MAPPING.checkInDetails]: 'check_in_date',
    [VALIDATION_FIELD_MAPPING.guest]: 'guest_name',
    [VALIDATION_FIELD_MAPPING.reservationStatus]: 'reservation_status',
    [VALIDATION_FIELD_MAPPING.hostPayout]: 'revenue',
    [VALIDATION_FIELD_MAPPING.status]: 'status',
    [VALIDATION_FIELD_MAPPING.source]: 'source',
  },
  invoiceList: {
    [VALIDATION_FIELD_MAPPING.invoiceNo]: 'invoice_no',
    [VALIDATION_FIELD_MAPPING.customer]: 'customer_name',
    [VALIDATION_FIELD_MAPPING.property]: 'property_name',
    [VALIDATION_FIELD_MAPPING.invoiceDate]: 'invoice_date',
    [VALIDATION_FIELD_MAPPING.dueDate]: 'due_date',
    [VALIDATION_FIELD_MAPPING.commission]: 'commission_amount',
    [VALIDATION_FIELD_MAPPING.invoiceAmount]: 'invoice_amount',
    [VALIDATION_FIELD_MAPPING.invoiceStatus]: 'status'
  },
  payoutList: {
    [VALIDATION_FIELD_MAPPING.property]: 'property_name',
    [VALIDATION_FIELD_MAPPING.hostPayout]: 'reservation_revenue',
    [VALIDATION_FIELD_MAPPING.payoutAmt]: 'payout_amount',
    [VALIDATION_FIELD_MAPPING.payoutMatchStatus]: 'reconciliation_status',
    [VALIDATION_FIELD_MAPPING.action]: 'reconciliation_verification',
  },
  invoiceReservationList: {
    [VALIDATION_FIELD_MAPPING.checkInDetails]: 'check_in_date',
    [VALIDATION_FIELD_MAPPING.guest]: 'guest_name',
    [VALIDATION_FIELD_MAPPING.reservationStatus]: 'reservation_status',
    [VALIDATION_FIELD_MAPPING.hostPayout]: 'revenue',
    [VALIDATION_FIELD_MAPPING.source]: 'source',
    [VALIDATION_FIELD_MAPPING.status]: 'status'
  },
  invoiceTransactionList: {
    [VALIDATION_FIELD_MAPPING.transactionDate]: 'date',
    [VALIDATION_FIELD_MAPPING.amount]: 'sub_total_amount',
    [VALIDATION_FIELD_MAPPING.status]: 'transaction_status',
    [VALIDATION_FIELD_MAPPING.lastUpdated]: 'updated_at'
  },
  invoicePaymentList: {
    [VALIDATION_FIELD_MAPPING.date]: 'payment_date',
    [VALIDATION_FIELD_MAPPING.amount]: 'payment_amount',
    [VALIDATION_FIELD_MAPPING.type]: 'payment_type',
    [VALIDATION_FIELD_MAPPING.method]: 'payment_method',
    [VALIDATION_FIELD_MAPPING.account]: 'account_id',
  }
};

export const HEAD_CELL_WIDTH_MAPPING = {
  userList: {
    [VALIDATION_FIELD_MAPPING.first_name]: '30%',
    [VALIDATION_FIELD_MAPPING.role]: '20%',
    [VALIDATION_FIELD_MAPPING.manager]: '20%',
    [VALIDATION_FIELD_MAPPING.status]: '20%',
    [VALIDATION_FIELD_MAPPING.actions]: '10%'
  },
  userAddEditProperties: {
    [VALIDATION_FIELD_MAPPING.name]: '20%',
    [VALIDATION_FIELD_MAPPING.address]: '20%',
    [VALIDATION_FIELD_MAPPING.actions]: '20%',
    [VALIDATION_FIELD_MAPPING.manager]: '20%',
  },
  userProfileProperties: {
    [VALIDATION_FIELD_MAPPING.image]: '10%',
    [VALIDATION_FIELD_MAPPING.name]: '30%',
    [VALIDATION_FIELD_MAPPING.city]: '30%',
    [VALIDATION_FIELD_MAPPING.managedBy]: '30%',
  },
  vendorList: {
    [VALIDATION_FIELD_MAPPING.name]: '25%',
    [VALIDATION_FIELD_MAPPING.phoneNumber]: '15%',
    [VALIDATION_FIELD_MAPPING.cities]: '20%',
    [VALIDATION_FIELD_MAPPING.serviceType]: '15%',
    [VALIDATION_FIELD_MAPPING.status]: '15%',
    [VALIDATION_FIELD_MAPPING.actions]: '15%'
  },
  customerList: {
    [VALIDATION_FIELD_MAPPING.primaryContact]: '25%',
    [VALIDATION_FIELD_MAPPING.primaryEmail]: '20%',
    [VALIDATION_FIELD_MAPPING.primaryPhone]: '20%',
    [VALIDATION_FIELD_MAPPING.city]: '15%',
    [VALIDATION_FIELD_MAPPING.status]: '10%',
    [VALIDATION_FIELD_MAPPING.actions]: '10%'
  },
  propertyList: {
    [VALIDATION_FIELD_MAPPING.name]: '25%',
    [VALIDATION_FIELD_MAPPING.customer]: '20%',
    [VALIDATION_FIELD_MAPPING.managedBy]: '20%',
    [VALIDATION_FIELD_MAPPING.commissionPercentage]: '15%',
    [VALIDATION_FIELD_MAPPING.status]: '10%',
    [VALIDATION_FIELD_MAPPING.actions]: '10%'
  },
  assignPropertyModalList: {
    [VALIDATION_FIELD_MAPPING.select]: '5%',
    [VALIDATION_FIELD_MAPPING.name]: '25%',
    [VALIDATION_FIELD_MAPPING.customer]: '20%',
    [VALIDATION_FIELD_MAPPING.managedBy]: '20%',
    [VALIDATION_FIELD_MAPPING.commissionPercentage]: '15%',
    [VALIDATION_FIELD_MAPPING.status]: '15%',
  },
  transactionList: {
    [VALIDATION_FIELD_MAPPING.transactionDate]: '8%',
    [VALIDATION_FIELD_MAPPING.propertyName]: '17%',
    [VALIDATION_FIELD_MAPPING.invoiceNumber]: '9%',
    [VALIDATION_FIELD_MAPPING.transactionType]: '13%',
    [VALIDATION_FIELD_MAPPING.debitCredit]: '11%',
    [VALIDATION_FIELD_MAPPING.amount]: '13%',
    [VALIDATION_FIELD_MAPPING.totalAmount]: '13%',
    [VALIDATION_FIELD_MAPPING.status]: '8%',
    [VALIDATION_FIELD_MAPPING.actions]: '8%',
  },
  platformListInPropertyForm: {
    [VALIDATION_FIELD_MAPPING.actions]: '15%',
    [VALIDATION_FIELD_MAPPING.name]: '15%',
    [VALIDATION_FIELD_MAPPING.whoCollectsMoney]: '35%',
    [VALIDATION_FIELD_MAPPING.whoCollectsHst]: '35%'
  },
  reservationList: {
    [VALIDATION_FIELD_MAPPING.property]: '15%',
    [VALIDATION_FIELD_MAPPING.checkInDetails]: '18%',
    [VALIDATION_FIELD_MAPPING.guest]: '10%',
    [VALIDATION_FIELD_MAPPING.reservationStatus]: '10%',
    [VALIDATION_FIELD_MAPPING.hostPayout]: '10%',
    [VALIDATION_FIELD_MAPPING.status]: '7%',
    [VALIDATION_FIELD_MAPPING.invoiceNumber]: '10%',
    [VALIDATION_FIELD_MAPPING.source]: '10%',
    [VALIDATION_FIELD_MAPPING.actions]: '10%',
  },
  invoiceList: {
    [VALIDATION_FIELD_MAPPING.invoiceStatus]: '10%',
    [VALIDATION_FIELD_MAPPING.invoiceNo]: '8%',
    [VALIDATION_FIELD_MAPPING.amountDue]: '10%',
    [VALIDATION_FIELD_MAPPING.customer]: '10%',
    [VALIDATION_FIELD_MAPPING.property]: '15%',
    [VALIDATION_FIELD_MAPPING.invoiceDate]: '10%',
    [VALIDATION_FIELD_MAPPING.dueDate]: '7%',
    [VALIDATION_FIELD_MAPPING.commission]: '6%',
    [VALIDATION_FIELD_MAPPING.invoiceAmount]: '9%',
    [VALIDATION_FIELD_MAPPING.actions]: '15%',
  },
  payoutList: {
    [VALIDATION_FIELD_MAPPING.select]: '3%',
    [VALIDATION_FIELD_MAPPING.property]: '14%',
    [VALIDATION_FIELD_MAPPING.reservation]: '16%',
    [VALIDATION_FIELD_MAPPING.hostPayout]: '10%',
    [VALIDATION_FIELD_MAPPING.payoutDetails]: '12%',
    [VALIDATION_FIELD_MAPPING.payoutAmt]: '9%',
    [VALIDATION_FIELD_MAPPING.payoutMatchStatus]: '9%',
    [VALIDATION_FIELD_MAPPING.action]: '8%',
    [VALIDATION_FIELD_MAPPING.noteAction]: '7%',
  },
  payoutAmtList: {
    [VALIDATION_FIELD_MAPPING.date]: '25%',
    [VALIDATION_FIELD_MAPPING.paymentDetails]: '50%',
    [VALIDATION_FIELD_MAPPING.amount]: '25%',
  },
  invoiceReservationList: {
    [VALIDATION_FIELD_MAPPING.actions]: '5%',
    [VALIDATION_FIELD_MAPPING.checkInDetails]: '25%',
    [VALIDATION_FIELD_MAPPING.guest]: '10%',
    [VALIDATION_FIELD_MAPPING.reservationStatus]: '15%',
    [VALIDATION_FIELD_MAPPING.hostPayout]: '15%',
    [VALIDATION_FIELD_MAPPING.source]: '10%',
    [VALIDATION_FIELD_MAPPING.status]: '10%',
    [VALIDATION_FIELD_MAPPING.lastSync]: '10%',
  },
  invoiceTransactionList: {
    [VALIDATION_FIELD_MAPPING.actions]: '5%',
    [VALIDATION_FIELD_MAPPING.transactionDate]: '10%',
    [VALIDATION_FIELD_MAPPING.debitCredit]: '10%',
    [VALIDATION_FIELD_MAPPING.transactionType]: '10%',
    [VALIDATION_FIELD_MAPPING.transactionDetails]: '15%',
    [VALIDATION_FIELD_MAPPING.actualAmount]: '10%',
    [VALIDATION_FIELD_MAPPING.amount]: '20%',
    [VALIDATION_FIELD_MAPPING.status]: '10%',
    [VALIDATION_FIELD_MAPPING.lastUpdated]: '10%',
  },
  invoicePaymentList: {
    [VALIDATION_FIELD_MAPPING.date]: '12%',
    [VALIDATION_FIELD_MAPPING.amount]: '12%',
    [VALIDATION_FIELD_MAPPING.type]: '12%',
    [VALIDATION_FIELD_MAPPING.method]: '12%',
    [VALIDATION_FIELD_MAPPING.account]: '15%',
    [VALIDATION_FIELD_MAPPING.notes]: '15%',
    [VALIDATION_FIELD_MAPPING.lastUpdated]: '10%',
    [VALIDATION_FIELD_MAPPING.actions]: '12%',
  },
  calculationTable: {
    [VALIDATION_FIELD_MAPPING.name]: '30%',
    [VALIDATION_FIELD_MAPPING.amount]: '25%',
    [VALIDATION_FIELD_MAPPING.platformCommission]: '25%',
    [VALIDATION_FIELD_MAPPING.net]: '20%',
  }
};

export const APP_ERROR_MESSAGES = {
  login: {
    blankUsername: translate.t('login.pageTexts.form.username.errors.required'),
    invalidUsername: translate.t('login.pageTexts.form.username.errors.invalid'),
    blankPassword: translate.t('login.pageTexts.form.password.errors.required'),
    shortPassword: translate.t('login.pageTexts.form.password.errors.lengthShort'),
    longPassword: translate.t('login.pageTexts.form.password.errors.lengthLong'),
  },
  forgotPassword: {
    blankEmail: translate.t('forgotPassword.pageTexts.form.email.errorMessage'),
  },
  resetPassword: {
    blankNewPassword: translate.t('resetPassword.pageTexts.form.newPassword.errors.required'),
    mismatchNewPassword: translate.t('resetPassword.pageTexts.form.newPassword.errors.mismatch'),
    blankConfirmPassword: translate.t('resetPassword.pageTexts.form.confirmPassword.errors.required'),
    mismatchConfirmPassword: translate.t('resetPassword.pageTexts.form.confirmPassword.errors.mismatch'),
    passwordFormat: translate.t('resetPassword.pageTexts.form.newPassword.errors.passwordFormat')
  },
  users: {
    addEdit: {
      blankFirstName: translate.t('users.addEdit.pageTexts.form.firstName.errors.required'),
      blankLastName: translate.t('users.addEdit.pageTexts.form.lastName.errors.required'),
      blankEmail: translate.t('users.addEdit.pageTexts.form.email.errors.required'),
      invalidEmail: translate.t('users.addEdit.pageTexts.form.email.errors.invalid'),
      blankPhoneNumber: translate.t('users.addEdit.pageTexts.form.phoneNumber.errors.required'),
      blankRole: translate.t('users.addEdit.pageTexts.form.role.errors.required')
    },
    changePassword: {
      blankCurrentPassword: translate.t('changePassword.pageTexts.form.currentPassword.errors.required'),
      blankNewPassword: translate.t('changePassword.pageTexts.form.newPassword.errors.required'),
      passwordFormat: translate.t('changePassword.pageTexts.form.newPassword.errors.passwordFormat'),
      blankConfirmPassword: translate.t('changePassword.pageTexts.form.confirmPassword.errors.required'),
      passwordsDoNotMatch: translate.t('changePassword.pageTexts.form.confirmPassword.errors.mismatch')
    }
  },
  vendors: {
    addEdit: {
      blankCompanyName: translate.t('vendor.addEdit.pageTexts.form.companyName.errors.required'),
      blankVendorName: translate.t('vendor.addEdit.pageTexts.form.name.errors.required'),
      blankServiceType: translate.t('vendor.addEdit.pageTexts.form.serviceType.errors.required'),
      blankPhoneNumber: translate.t('vendor.addEdit.pageTexts.form.phoneNumber.errors.required'),
      invalidEmail: translate.t('vendor.addEdit.pageTexts.form.email.errors.invalid'),
      blankServiceRate: translate.t('vendor.addEdit.pageTexts.form.serviceRate.errors.required'),
      invalidServiceRate: translate.t('vendor.addEdit.pageTexts.form.serviceRate.errors.invalid'),
      maxLengthExceeded: translate.t('common.maxLength.errors.exceeded'),
    }
  },
  customers: {
    addEdit: {
      blankCustomerName: translate.t('customers.addEdit.pageTexts.form.customer.errors.required'),
      blankPrimaryFirstName: translate.t('customers.addEdit.pageTexts.form.primaryFirstName.errors.required'),
      blankPrimaryLastName: translate.t('customers.addEdit.pageTexts.form.primaryLastName.errors.required'),
      blankPrimaryEmail: translate.t('customers.addEdit.pageTexts.form.primaryEmail.errors.required'),
      invalidPrimaryEmail: translate.t('customers.addEdit.pageTexts.form.primaryEmail.errors.invalid'),
      invalidSecondaryEmail: translate.t('customers.addEdit.pageTexts.form.secondaryEmail.errors.invalid'),
      blankCountryCode: translate.t('customers.addEdit.pageTexts.form.countryCode.errors.required'),
      blankDisplayName: translate.t('customers.addEdit.pageTexts.form.displayName.errors.required'),
      blankBillingCurrency: translate.t('customers.addEdit.pageTexts.form.billingCurrency.errors.required'),
      blankPhoneNumber: translate.t('customers.addEdit.pageTexts.form.phoneNumber.errors.required'),
      maxLengthExceeded: translate.t('common.maxLength.errors.exceeded'),

    }
  },
  property: {
    addEdit: {
      blankCustomer: translate.t('property.addEdit.pageTexts.form.customer.errors.required'),
      blankPropertyName: translate.t('property.addEdit.pageTexts.form.name.errors.required'),
      blankPublicName: translate.t('property.addEdit.pageTexts.form.publicName.errors.required'),
      blankHospitableId: translate.t('property.addEdit.pageTexts.form.hospitableId.errors.required'),
      invalidPictureUrl: translate.t('property.addEdit.pageTexts.form.pictureUrl.errors.invalid'),
      blankManagedBy: translate.t('property.addEdit.pageTexts.form.managedBy.errors.required'),
      blankCleaningManagedBy: translate.t('property.addEdit.pageTexts.form.cleaningManagedBy.errors.required'),
      blankWhoCollectsMoney: translate.t('property.addEdit.pageTexts.form.whoCollectsMoney.errors.required'),
      blankWhoCollectsHst: translate.t('property.addEdit.pageTexts.form.whoCollectsHst.errors.required'),
      blankMatManagedBy: translate.t('property.addEdit.pageTexts.form.matManagedBy.errors.required'),
      blankAddress: translate.t('property.addEdit.pageTexts.form.address1.errors.required'),
      blankCity: translate.t('property.addEdit.pageTexts.form.city.errors.required'),
      blankState: translate.t('property.addEdit.pageTexts.form.state.errors.required'),
      blankPostcode: translate.t('property.addEdit.pageTexts.form.postal.errors.required'),
      blankCountry: translate.t('property.addEdit.pageTexts.form.country.errors.required'),
      blankBookingType: translate.t('property.addEdit.pageTexts.form.bookingType.errors.required'),
      blankCommissionPercentage: translate.t('property.addEdit.pageTexts.form.commission.errors.required'),
      blankMaintenanceDetails: translate.t('property.addEdit.pageTexts.form.maintenanceDetails.errors.required'),
      invalidCommissionPercentage: translate.t('property.addEdit.pageTexts.form.commission.errors.invalid'),
      blankMatTaxPercentage: translate.t('property.addEdit.pageTexts.form.matTaxPercentage.errors.required'),
      invalidMatTaxPercentage: translate.t('property.addEdit.pageTexts.form.matTaxPercentage.errors.invalid'),
      maxLengthExceeded: translate.t('common.maxLength.errors.exceeded'),
      pictureUrlMaxLengthExceeded: translate.t('common.maxLength.errors.pictureUrlExceeded')
    }
  },
  transactions: {
    addEdit: {
      blankTransactionDate: translate.t('transactions.addEdit.pageTexts.form.transactionDate.errors.required'),
      blankProperty: translate.t('transactions.addEdit.pageTexts.form.property.errors.required'),
      blankDebitCredit: translate.t('transactions.addEdit.pageTexts.form.debitCredit.errors.required'),
      blankTransactionType: translate.t('transactions.addEdit.pageTexts.form.transactionType.errors.required'),
      blankReviewedBy: translate.t('transactions.addEdit.pageTexts.form.reviewedBy.errors.required'),
      blankNotes: translate.t('transactions.addEdit.pageTexts.form.notes.errors.required'),
      invalidActualCost: translate.t('transactions.addEdit.pageTexts.form.actualCost.errors.invalid'),
      blankTotalAmount: translate.t('transactions.addEdit.pageTexts.form.totalAmount.errors.required'),
      invalidTotalAmount: translate.t('transactions.addEdit.pageTexts.form.totalAmount.errors.invalid'),
      blankSubTotal: translate.t('transactions.addEdit.pageTexts.form.subTotal.errors.required'),
      blankExpenseDetails: translate.t('transactions.addEdit.pageTexts.form.expenseDetails.errors.required'),
      invalidSubTotal: translate.t('transactions.addEdit.pageTexts.form.subTotal.errors.invalid'),
      maxLengthExceeded: translate.t('common.maxLength.errors.exceeded'),
      invalidAttachmentType: translate.t('transactions.addEdit.pageTexts.form.attachments.errors.invalidType'),
      attachmentTooLarge: translate.t('transactions.addEdit.pageTexts.form.attachments.errors.tooLarge'),
      removeAttachmentFailed: translate.t('transactions.addEdit.pageTexts.form.attachments.errors.removeFailed'),
      openAttachmentFailed: translate.t('transactions.addEdit.pageTexts.form.attachments.errors.openFailed'),
    }
  },
  reservations: {
    edit: {
      blankCheckInDate: translate.t('reservations.edit.pageTexts.form.checkInDate.errors.required'),
      blankCheckOutDate: translate.t('reservations.edit.pageTexts.form.checkOutDate.errors.required'),
      blankReviewedBy: translate.t('reservations.edit.pageTexts.form.reviewedBy.errors.required'),
      blankNotes: translate.t('reservations.edit.pageTexts.form.notes.errors.required'),
      invalidXNightRoomFees: translate.t('reservations.edit.pageTexts.form.xNightRoomFees.errors.invalid'),
      invalidPerNightFees: translate.t('reservations.edit.pageTexts.form.perNightFees.errors.invalid'),
      invalidCleaningFees: translate.t('reservations.edit.pageTexts.form.cleaningFees.errors.invalid'),
      invalidLinenFees: translate.t('reservations.edit.pageTexts.form.linenFees.errors.invalid'),
      invalidPetFees: translate.t('reservations.edit.pageTexts.form.petFees.errors.invalid'),
      invalidHostFees: translate.t('reservations.edit.pageTexts.form.hostFees.errors.invalid'),
      invalidDiscount: translate.t('reservations.edit.pageTexts.form.discount.errors.invalid'),
      invalidMatTax: translate.t('reservations.edit.pageTexts.form.matTax.errors.invalid'),
      invalidHstTax: translate.t('reservations.edit.pageTexts.form.hstTax.errors.invalid'),
      invalidAdjustments: translate.t('reservations.edit.pageTexts.form.adjustments.errors.invalid'),
      invalidHostPayout: translate.t('reservations.edit.pageTexts.form.hostPayout.errors.invalid'),
      invalidActualRevenue: translate.t('reservations.edit.pageTexts.form.actualRevenue.errors.invalid'),
      invalidPlatformCommission: translate.t('reservations.edit.pageTexts.form.configurationModal.platformCommissionErrors.invalid'),
      blankPlatformCommission: translate.t('reservations.edit.pageTexts.form.configurationModal.platformCommissionErrors.required'),
      maxLengthExceeded: translate.t('common.maxLength.errors.exceeded'),
    }
  },
  invoices: {
    addList: {
      blankProperty: translate.t('invoices.addList.pageTexts.form.property.errors.required'),
      blankMonth: translate.t('invoices.addList.pageTexts.form.month.errors.required'),
      blankYear: translate.t('invoices.addList.pageTexts.form.year.errors.required')
    },
    editPreviewPayment: {
      blankDate: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.date.errors.required'),
      blankAmount: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.amount.errors.required'),
      invalidAmount: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.amount.errors.invalid'),
      blankMethod: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.method.errors.required'),
      blankAccount: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.account.errors.required'),
      blankNotes: translate.t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.notes.errors.required'),
      blankReviewedBy: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.reviewedBy.errors.required'),
      blankNotesDetails: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.otherDetails.notes.errors.required')
    }
  },
  reconciliation: {
    payout: {
      blankAction: translate.t('reconciliation.payout.list.pageTexts.modals.note.fields.action.errors.required'),
      blankNotes: translate.t('reconciliation.payout.list.pageTexts.modals.note.fields.notes.errors.required'),
    }
  }
};

export const APP_SUCCESS_MESSAGES = {
  userCreated: translate.t('users.addEdit.pageTexts.form.addSuccess'),
  userUpdated: translate.t('users.addEdit.pageTexts.form.editSuccess'),
  vendorCreated: translate.t('vendor.addEdit.pageTexts.form.addSuccess'),
  vendorUpdated: translate.t('vendor.addEdit.pageTexts.form.editSuccess'),
  customerCreated: translate.t('customers.addEdit.pageTexts.form.addSuccess'),
  customerUpdated: translate.t('customers.addEdit.pageTexts.form.editSuccess'),
  propertyCreated: translate.t('property.addEdit.pageTexts.form.addSuccess'),
  propertyUpdated: translate.t('property.addEdit.pageTexts.form.editSuccess'),
  transactionCreated: translate.t('transactions.addEdit.pageTexts.form.addSuccess'),
  transactionUpdated: translate.t('transactions.addEdit.pageTexts.form.editSuccess'),
  transactionDeleted: "Transaction deleted successfully.",
  transactionDeleteRequestSent: "Deletion request sent successfully.",
  reservationUpdated: translate.t('reservations.edit.pageTexts.form.editSuccess'),
  invoiceDetailsUpdated: translate.t('invoices.editPreviewPayment.pageTexts.form.detailsTab.editSuccess'),
  syncReservationDispatched: translate.t('reservations.list.pageTexts.syncDispatchedSuccess'),
  syncPayoutDispatched: translate.t('reconciliation.payout.list.pageTexts.syncDispatchedSuccess'),
  changePassword: translate.t('changePassword.pageTexts.form.success'),
  forgotPassword: translate.t('forgotPassword.pageTexts.form.success'),
  resetPassword: translate.t('resetPassword.pageTexts.form.success'),
  propertyAssigned: translate.t('users.addEdit.pageTexts.form.propertyAssignSuccess'),
  userAssigned: translate.t('property.addEdit.pageTexts.form.userAssignSuccess'),
  invoiceCreated: translate.t('invoices.addList.pageTexts.form.addSuccess'),
  invoiceCompleted: translate.t('invoices.editPreviewPayment.pageTexts.invoiceCompleted'),
  emailSent: translate.t('invoices.editPreviewPayment.pageTexts.emailSent'),
}

export const MaxLength = {
  login: {
    password: 15,
  },
  pageSize: 200,
  common: 100,
  pictureUrl: 250
};

export const MinLength = {
  login: {
    password: 6,
  }
};

export const ATTACHMENT = {
  maxSizeBytes: 4 * 1024 * 1024, // 4MB — matches the server-side limit (AC9 enforces this independently)
  allowedExtensions: ['jpg', 'jpeg', 'png', 'heic', 'jfif', 'pdf'],
  chunkSizeBytes: 1 * 1024 * 1024, // 1MB per chunk
  maxRetriesPerChunk: 3,
};

export const LOCAL_STORAGE_KEYS: any = {
  loggedInUser: 'loggedInUser',
  token: 'token',
  theme: 'theme',
  permissions: 'permissions',
  isSystemAdmin: 'isSystemAdmin',
  exception: 'exception',
  loading: 'loading',
  confirmationFormRouting: 'confirmationFormRouting'
};

export const GENERIC_LOOKUP_KEYS = {
  status: 'status',
  salutation: 'salutation',
  billingCurrency: 'billing_currency',
  propertyBookingType: 'property_booking_type',
  whoCollectsMoney: 'who_collects_money',
  whoCollectsHst: 'who_collects_hst',
  whoCollectsMat: 'who_collects_mat',
  transactionType: 'transaction_type',
  debitCredit: 'crdr',
  hstPercentage: 'tax',
  transactionStatus: 'transaction_status',
  crdr: 'crdr',
  reservationStatus: 'reservation_status',
  invoiceStatus: 'invoice_status',
  source: 'source',
  linkedWithHospitable: 'yes_no',
  month: 'month',
  paymentDate: 'payment_date',
  paymentAmount: 'payment_amount',
  paymentType: 'payment_type',
  paymentMethod: 'payment_method',
  paymentAccount: 'payment_account',
  verificationStatus: 'verification_status',
  reconciliationVerificationStatus: 'reconciliation_verification_status',
  reconciliationPayoutStatus: 'reconciliation_payout_status',
  chargeableFilter: 'chargeable_filter',
  paidBy: 'paid_by',
};

export const WHO_COLLECTS_MAT = {
  cohost: 'cohost',
  customer: 'customer',
  platform: 'platform',
  notApplicable: 'not_applicable',
};

export const DEFAULTS = {
  country: '1',
  state: '1',
  currency: 'CAD',
  status: 'unbilled',
  statusLabel: 'Unbilled',
  debit: 'dr',
  hst: '13.00',
  paymentType: 'received',
  paymentMethod: 'bank_payment',
  paymentAccount: 'VENN',
  month: () => getCurrentMonthAndYear().month,
  year: () => getCurrentMonthAndYear().year
}

export const VALIDATION_TYPE = {
  email: 'EMAIL'
};

export const RESERVATION_STATUS = {
  accepted: 'accepted',
  expired: 'expired',
  cancelled: 'cancelled',
  unbilled: 'unbilled',
  reconciled: 'reconciled'
}

export const TRANSACTION_STATUS = {
  unbilled: 'unbilled',
  invoiced: 'invoiced'
}

export const INVOICE_STATUS = {
  reconciled: 'reconciled',
  draft: 'draft',
  sent: 'sent',
  overdue: 'overdue',
  invoiced: 'invoiced',
  completed: 'completed'
}

export const SOURCE = {
  hospitable: 'hospitable',
  manual: 'manual'
}

export const PAYMENT_ACCOUNTS = {
  OTHER: "OTHER"
}

export const PAYMENT_TYPES = {
  received: 'received',
  paid: 'paid'
}

export const PERMISSION_MATRIX = [
  {
    "Module": "User",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true
      },
      "Manager": {
        "Read": false,
        "Create": false,
        "Update": false
      },
      "Support": {
        "Read": false,
        "Create": false,
        "Update": false
      }
    }
  },
  {
    "Module": "Customer",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true
      },
      "Manager": {
        "Read": false,
        "Create": false,
        "Update": false
      },
      "Support": {
        "Read": false,
        "Create": false,
        "Update": false
      }
    }
  },
  {
    "Module": "Property",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true
      },
      "Manager (Assigned)": {
        "Read": true,
        "Create": false,
        "Update": false
      },
      "Support (Assigned)": {
        "Read": true,
        "Create": false,
        "Update": false
      }
    }
  },
  {
    "Module": "Vendor",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true
      },
      "Manager": {
        "Read": true,
        "Create": true,
        "Update": true
      },
      "Support": {
        "Read": true,
        "Create": true,
        "Update": true
      }
    }
  },
  {
    "Module": "Transaction",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": true
      },
      "Manager (Assigned Properties)": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": false
      },
      "Support (Assigned Properties)": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": false
      }
    }
  },
  {
    "Module": "Reservation",
    "Roles": {
      "Admin": {
        "Read": true,
        "Update": true,
        "Active": true
      },
      "Manager (Assigned Properties)": {
        "Read": true,
        "Update": true,
        "Active": false
      },
      "Support (Assigned Properties)": {
        "Read": true,
        "Update": false,
        "Active": false
      }
    }
  },
  {
    "Module": "Invoices",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": true,
        "Send Email": true,
        "Mark invoice as Completed": true,
        "Verify invoice": true
      },
      "Manager (Assigned Properties)": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": false,
        "Send Email": true,
        "Mark invoice as Completed": false,
        "Verify invoice": false
      },
      "Support (Assigned Properties)": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": false,
        "Send Email": false,
        "Mark invoice as Completed": false,
        "Verify invoice": false
      }
    }
  },
  {
    "Module": "Payment",
    "Roles": {
      "Admin": {
        "Read": true,
        "Create": true,
        "Update": true,
        "Delete": true
      },
      "Manager (Assigned Properties)": {
        "Read": true,
        "Create": false,
        "Update": false,
        "Delete": false
      },
      "Support (Assigned Properties)": {
        "Read": true,
        "Create": false,
        "Update": false,
        "Delete": false
      }
    }
  },
  {
    "Module": "Payout",
    "Roles": {
      "Admin": {
        "Read": true,
        "Update": true
      },
      "Manager (Assigned Properties)": {
        "Read": true,
        "Update": true
      },
      "Support (Assigned Properties)": {
        "Read": false,
        "Update": false
      }
    }
  }
]
